import { QueryClient, useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import useLog from 'src/hooks/useLog';
import { useFileUploader } from 'src/hooks/useFileUploader';
import { get, post, put, destroy } from '../utils';
import type {
  OAuthClient,
  OAuthClientGrant,
  OAuthClientWithSecret,
  CreateOAuthClientRequest,
  UpdateOAuthClientRequest,
  OAuthClientStatus,
} from '../../../types/OAuthClient';

enum QueryKeys {
  OAuthClients = 'OAuthClients',
  OAuthClient = 'OAuthClient',
  OAuthClientGrants = 'OAuthClientGrants',
}

/** Shared mutationKey tagging every mutation whose result carries the plaintext client secret (create +
 *  rotate). purgeOAuthClientSecretMutations matches on it so an IN-FLIGHT mutation — whose state.data is
 *  still undefined at unmount — is removed too, not just settled ones. */
export const OAUTH_CLIENT_SECRET_MUTATION_KEY = 'oauthClientSecret';

export function useQueryGetOAuthClients(): UseQueryResult<OAuthClient[]> {
  const log = useLog();
  return useQuery([QueryKeys.OAuthClients], () => get<OAuthClient[]>('oauthclient'), {
    onError: (err) => {
      if (err instanceof Error) {
        log.error(`There was a problem loading OAuth clients: ${err.message}`, err);
      }
    },
  });
}

export function useQueryGetOAuthClient(clientId: string): UseQueryResult<OAuthClient> {
  const log = useLog();
  return useQuery([QueryKeys.OAuthClient, clientId], () => get<OAuthClient>(`oauthclient/${clientId}`), {
    enabled: Boolean(clientId),
    onError: (err) => {
      if (err instanceof Error) {
        log.error(`There was a problem loading the OAuth client: ${err.message}`, err);
      }
    },
  });
}

/** Mints a presigned PUT into the public photo bucket and returns the canonical public URL to echo
 *  back in the create/edit request body. Content-Type is pinned server-side to the passed value, and
 *  fileSize (bytes) lets the API enforce its server-side size cap before minting the presigned PUT. */
export async function getOAuthClientLogoUploadUrl(
  contentType: string,
  fileSize: number,
): Promise<{ uploadUrl: string; logoUrl: string }> {
  return post<{ uploadUrl: string; logoUrl: string }>('oauthclient/logo-upload-url', { contentType, fileSize });
}

/** Two-step logo upload (mirrors the QuickCircuit attachment precedent in customer-order-attachment.ts):
 *  ask the API for a presigned PUT keyed to the file's content type, raw-PUT the bytes to S3, then
 *  resolve the public logoUrl the caller holds in Formik and sends with create/edit. No query cache to
 *  touch — the logo isn't persisted until the surrounding create/edit request runs. */
export function useUploadOAuthClientLogoMutation() {
  const { uploadFile } = useFileUploader();
  return useMutation<string, Error, File>(async (file: File) => {
    const { uploadUrl, logoUrl } = await getOAuthClientLogoUploadUrl(file.type, file.size);
    const uploadSucceeded = await uploadFile(uploadUrl, file);
    if (!uploadSucceeded) {
      throw new Error(`Failed to upload ${file.name}`);
    }
    return logoUrl;
  });
}

export function useCreateOAuthClientMutation() {
  const queryClient = useQueryClient();
  return useMutation((data: CreateOAuthClientRequest) => post<OAuthClientWithSecret>('oauthclient', data), {
    mutationKey: OAUTH_CLIENT_SECRET_MUTATION_KEY,
    // Seed the freshly-created client's detail cache for instant navigation. No single-client
    // invalidation is needed on create (no detail page is mounted yet); onSettled reconciles the LIST.
    onSuccess: (created) => {
      queryClient.setQueryData([QueryKeys.OAuthClient, created.client.id], created.client);
    },
    onSettled: () => {
      queryClient.invalidateQueries([QueryKeys.OAuthClients], {}, { cancelRefetch: true });
    },
  });
}

// Concurrent-write reconciliation pattern — shared by every single-client mutation below.
// onSuccess's setQueryData paints the server response into the detail cache immediately (instant,
// optimistic UI), while onSettled — which fires on BOTH success AND error — invalidates the
// single-client and list queries with { cancelRefetch: true }. The flag is load-bearing under
// racing mutations: v3's BARE invalidate → refetchQueries → query.fetch DEDUPES onto an
// already-in-flight refetch (fetch returns the existing promise when state.isFetching), so a GET
// started by an earlier settle — whose server read predates a later write — could land last and
// clobber the newer optimistic value. cancelRefetch: true instead makes query.fetch SILENTLY CANCEL
// the in-flight refetch and issue a fresh GET, so the reconciling read is dispatched AFTER the final
// write and server truth (not a stale in-flight read) wins the last-write-wins race. onSettled
// firing on error also reconciles a cache left stale by a failed/conflicted write.
// (Call form: invalidateQueries(queryKey, filters, options) — v3 parseFilterArgs only reads
// cancelRefetch from the 3rd arg; in the 2nd it merges into filters and never reaches fetch.)
export function useUpdateOAuthClientMutation(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation((data: UpdateOAuthClientRequest) => put<OAuthClient>(`oauthclient/${clientId}`, { ...data }), {
    onSuccess: (updated) => {
      queryClient.setQueryData([QueryKeys.OAuthClient, clientId], updated);
    },
    onSettled: () => {
      queryClient.invalidateQueries([QueryKeys.OAuthClient, clientId], {}, { cancelRefetch: true });
      queryClient.invalidateQueries([QueryKeys.OAuthClients], {}, { cancelRefetch: true });
    },
  });
}

export function useRotateOAuthClientSecretMutation(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation(() => put<OAuthClientWithSecret>(`oauthclient/${clientId}/secret`, {}), {
    mutationKey: OAUTH_CLIENT_SECRET_MUTATION_KEY,
    onSuccess: (rotated) => {
      queryClient.setQueryData([QueryKeys.OAuthClient, clientId], rotated.client);
    },
    onSettled: () => {
      queryClient.invalidateQueries([QueryKeys.OAuthClient, clientId], {}, { cancelRefetch: true });
      queryClient.invalidateQueries([QueryKeys.OAuthClients], {}, { cancelRefetch: true });
    },
  });
}

export function useSetOAuthClientStatusMutation(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation((status: OAuthClientStatus) => put<OAuthClient>(`oauthclient/${clientId}/status`, { status }), {
    onSuccess: (updated) => {
      queryClient.setQueryData([QueryKeys.OAuthClient, clientId], updated);
    },
    onSettled: () => {
      queryClient.invalidateQueries([QueryKeys.OAuthClient, clientId], {}, { cancelRefetch: true });
      queryClient.invalidateQueries([QueryKeys.OAuthClients], {}, { cancelRefetch: true });
    },
  });
}

export function useSetOAuthClientRateLimitMutation(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    (rateLimitPerMinute: number | null) => put<OAuthClient>(`oauthclient/${clientId}/rate-limit`, { rateLimitPerMinute }),
    {
      onSuccess: (updated) => {
        queryClient.setQueryData([QueryKeys.OAuthClient, clientId], updated);
      },
      onSettled: () => {
        queryClient.invalidateQueries([QueryKeys.OAuthClient, clientId], {}, { cancelRefetch: true });
        queryClient.invalidateQueries([QueryKeys.OAuthClients], {}, { cancelRefetch: true });
      },
    },
  );
}

export function useQueryGetOAuthClientGrants(clientId: string): UseQueryResult<OAuthClientGrant[]> {
  const log = useLog();
  return useQuery([QueryKeys.OAuthClientGrants, clientId], () => get<OAuthClientGrant[]>(`oauthclient/${clientId}/grants`), {
    enabled: Boolean(clientId),
    onError: (err) => {
      if (err instanceof Error) {
        log.error(`There was a problem loading grants for this client: ${err.message}`, err);
      }
    },
  });
}

export function useRevokeOAuthClientGrantMutation(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation((grantId: string) => destroy(`oauthclient/${clientId}/grants/${grantId}`), {
    // onSettled (not onSuccess): a failed revoke — e.g. a 404 because the grant was already gone —
    // proves the grants cache is stale, so reconcile against the server on error too. Refetch drops
    // the phantom row a success-only invalidation would have left behind.
    onSettled: () => {
      queryClient.invalidateQueries([QueryKeys.OAuthClientGrants, clientId], {}, { cancelRefetch: true });
    },
  });
}

/** react-query v3 keeps settled mutation results in its MutationCache indefinitely (no GC until
 *  page reload). Create/rotate results carry the plaintext client secret, so once the secret has
 *  been shown — or the page unmounts — purge those mutations outright (observer reset() would leave
 *  the data in cache). Two match rules cover the two states a secret-bearing mutation can be in:
 *   - `OAUTH_CLIENT_SECRET_MUTATION_KEY` on options — catches an IN-FLIGHT create/rotate whose
 *     state.data is still undefined at unmount (the case a settled-data-only predicate would miss).
 *     mutationCache.remove() drops it from the internal array AND calls mutation.cancel(), which
 *     resolves the retryer with a CancelledError — the late-arriving HTTP result is then discarded
 *     by the retryer's isResolved guard, so {client, secret} never lands anywhere (verified against
 *     react-query 3.39.3 mutationCache.remove / retryer.cancel).
 *   - `state.data?.secret` — belt-and-braces for any settled secret-bearing mutation, even one
 *     somehow missing the key. */
export function purgeOAuthClientSecretMutations(queryClient: QueryClient): void {
  const mutationCache = queryClient.getMutationCache();
  // Snapshot the list first: remove() reassigns the cache's internal array, so iterating a copy
  // keeps us safe regardless of react-query's internal removal mechanics.
  [...mutationCache.getAll()].forEach((mutation) => {
    const data = mutation.state.data as { secret?: unknown } | undefined;
    const isSecretMutation = mutation.options.mutationKey === OAUTH_CLIENT_SECRET_MUTATION_KEY;
    if (isSecretMutation || data?.secret) {
      mutationCache.remove(mutation);
    }
  });
}
